/**
 * Resolución de salida de una imagen generada — 1K, 2K o 4K.
 *
 * **Espejo del backend**: `atlas-backend/src/billing/costs.ts` (`IMAGE_SIZES`,
 * `imageCreditsFor`). El valor de `id` es literalmente el que viaja en el body como
 * `image_size` y el que el backend le pasa a Gemini en `imageConfig.imageSize`.
 * Si cambia la tarifa allá, cambia acá.
 *
 * La tarifa sale del costo real medido en producción (1K $0,085 · 2K $0,119 ·
 * 4K $0,169) contra el ancla de ~$0,08 por crédito de `utils/credits.ts`.
 * Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k.
 *
 * ⚠️ Lo que se muestra acá es lo que se RESERVA. El cobro real puede terminar siendo
 * menor: el backend cobra por la resolución que efectivamente entregó, así que si la
 * cadena cae a otro proveedor que no llega a 4K, el usuario paga por lo que recibió.
 * Nunca al revés — lo pedido es el techo.
 */
export const RESOLUCIONES = ['1K', '2K', '4K'] as const;

export type ResolucionImagen = (typeof RESOLUCIONES)[number];

/** La de siempre: ningún flujo cambia de costo mientras no toquen el selector. */
export const RESOLUCION_DEFAULT: ResolucionImagen = '1K';

const CREDITOS: Record<ResolucionImagen, number> = { '1K': 1, '2K': 2, '4K': 3 };

/** Créditos que cuesta UNA imagen en esta resolución. */
export function creditosPorResolucion(res: ResolucionImagen | null | undefined): number {
  return CREDITOS[normalizarResolucion(res)];
}

/** Sanea cualquier valor a una resolución válida. Ante la duda, la barata. */
export function normalizarResolucion(
  valor: string | null | undefined,
): ResolucionImagen {
  const v = String(valor ?? '').trim().toUpperCase();
  return (RESOLUCIONES as readonly string[]).includes(v)
    ? (v as ResolucionImagen)
    : RESOLUCION_DEFAULT;
}

export interface OpcionResolucion {
  id: ResolucionImagen;
  /** Lo que se ve en la fila y en el menú. */
  label: string;
  /** Para qué sirve, en una línea. */
  detalle: string;
}

export const OPCIONES_RESOLUCION: OpcionResolucion[] = [
  { id: '1K', label: 'Estándar', detalle: 'La de siempre. Perfecta para redes.' },
  { id: '2K', label: 'Alta (2K)', detalle: 'Cuatro veces más detalle. Para imprimir o hacer zoom.' },
  { id: '4K', label: 'Máxima (4K)', detalle: 'Todo el detalle. Para gráfica grande.' },
];

/** Etiqueta de una resolución (la que muestra la fila cerrada). */
export function labelResolucion(res: ResolucionImagen | null | undefined): string {
  const id = normalizarResolucion(res);
  return OPCIONES_RESOLUCION.find((o) => o.id === id)?.label ?? id;
}
